import React, { useState, useCallback, useEffect, useRef } from "react";
import "./style/qrScannerCard.scss";
import { Button } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { QRScanner } from "../../media/QRScanner/qrScanner";
import { useAppConfig } from "../../../hooks/useAppConfig";
import { useCamera } from "../../../hooks/useCamera";
import { useStrings } from "../../../hooks/useStrings";

// EN-3997: bring the capture-page camera-hardware handling to the QR scanner.
// Silent recovery for transient camera-busy errors (NotReadableError /
// TrackStartError) mirrors VisitorPicture: remount the scanner a couple of
// times before falling back to a manual retry button.
const MAX_AUTO_RETRIES = 2;
const RETRY_DELAYS_MS = [600, 1500];

type CameraErrorKind = "denied" | "busy" | "notfound" | "init";

interface QRScannerCardProps {
  onCodeResult: (decodedText: string) => void;
  onError?: (message: string) => void;
  onPermissionChange?: (hasPermission: boolean) => void;
  isLoading?: boolean;
}

export const QRScannerCard: React.FC<QRScannerCardProps> = ({
  onCodeResult,
  onError,
  onPermissionChange,
  isLoading = false,
}) => {
  const { themeColors } = useAppConfig();
  const S = useStrings();
  const { permissionState, requestPermission } = useCamera();

  // Camera-busy recovery (EN-3997). The scanner owns the camera device, so we
  // recover by remounting <QRScanner> via scannerKey rather than probing
  // getUserMedia in parallel (which would contend for the device).
  const [scannerKey, setScannerKey] = useState(0);
  const [cameraStatus, setCameraStatus] = useState<"ok" | "retrying" | "failed">("ok");
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only treat cameraStatus as meaningful while permission is granted — any
  // other permission state takes over the UI through its own branch below.
  const effectiveCameraStatus =
    permissionState === "granted" ? cameraStatus : "ok";

  // Reset the busy-recovery state the moment permission (re-)becomes "granted",
  // so a fresh grant always starts the scanner clean instead of inheriting a
  // stale "failed"/"retrying". Done during render via the prev-value pattern
  // (React-sanctioned) rather than in an effect, to avoid cascading renders.
  const [prevPermissionState, setPrevPermissionState] = useState(permissionState);
  if (permissionState !== prevPermissionState) {
    setPrevPermissionState(permissionState);
    if (permissionState === "granted") {
      setCameraStatus("ok");
    }
  }

  // Cancel any pending auto-retry timer on unmount so we don't bump the
  // scanner key after the user has navigated away.
  useEffect(() => () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // On entering "granted", restore auto-retries; on leaving, drop any pending
  // retry timer. Ref writes only — the render-phase guard above owns setState.
  useEffect(() => {
    if (permissionState === "granted") {
      retryCountRef.current = 0;
    } else if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [permissionState]);

  const handleScannerPermissionChange = useCallback(
    (hasPermission: boolean) => {
      if (hasPermission) {
        retryCountRef.current = 0;
        setCameraStatus("ok");
      }
      onPermissionChange?.(hasPermission);
    },
    [onPermissionChange],
  );

  const handleCameraError = useCallback(
    ({ kind }: { kind: CameraErrorKind; raw?: DOMException }) => {
      if (kind === "denied") {
        // Re-trigger the OS prompt; useCamera will reconcile permissionState.
        requestPermission();
        return;
      }

      if (kind === "notfound") {
        setCameraStatus("failed");
        onError?.(S.QR_SCANNER_CARD.PERMISSION_UNSUPPORTED);
        return;
      }

      if (kind === "init") {
        setCameraStatus("failed");
        onError?.(S.QR_SCANNER_CARD.ERROR_CAMERA_INIT_FAILED);
        return;
      }

      // kind === "busy": silently remount the scanner up to MAX_AUTO_RETRIES.
      if (retryCountRef.current < MAX_AUTO_RETRIES) {
        const delay = RETRY_DELAYS_MS[retryCountRef.current];
        retryCountRef.current += 1;
        setCameraStatus("retrying");
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          setCameraStatus("ok");
          setScannerKey((k) => k + 1);
        }, delay);
      } else {
        setCameraStatus("failed");
        onError?.(S.QR_SCANNER_CARD.ERROR_CAMERA_IN_USE);
      }
    },
    [
      requestPermission,
      onError,
      S.QR_SCANNER_CARD.PERMISSION_UNSUPPORTED,
      S.QR_SCANNER_CARD.ERROR_CAMERA_INIT_FAILED,
      S.QR_SCANNER_CARD.ERROR_CAMERA_IN_USE,
    ],
  );

  const handleManualRetry = useCallback(() => {
    // After a manual retry, skip silent auto-retries — any further failure
    // should surface immediately.
    retryCountRef.current = MAX_AUTO_RETRIES;
    setCameraStatus("ok");
    setScannerKey((k) => k + 1);
  }, []);

  const handleEnableCamera = useCallback(() => {
    requestPermission();
  }, [requestPermission]);

  const permissionMessage =
    permissionState === "denied"
      ? S.QR_SCANNER_CARD.PERMISSION_DENIED
      : permissionState === "unsupported"
        ? S.QR_SCANNER_CARD.PERMISSION_UNSUPPORTED
        : S.QR_SCANNER_CARD.PERMISSION_PROMPT;

  return (
    <>
      {/* Live scanner — only while permission is granted and the camera is ok */}
      {permissionState === "granted" && effectiveCameraStatus === "ok" && (
        <div className="qr-scan-wrapper" data-testid="qrscannercard-qr-scan-area">
          <span className="qr-corner qr-corner-tl" />
          <span className="qr-corner qr-corner-tr" />
          <span className="qr-corner qr-corner-bl" />
          <span className="qr-corner qr-corner-br" />
          <div className="qr-scan-area">
            <QRScanner
              key={scannerKey}
              isActive
              onCodeResult={onCodeResult}
              onError={onError}
              onCameraError={handleCameraError}
              onPermissionChange={handleScannerPermissionChange}
            />
          </div>
          <div className="qr-scan-line" />
        </div>
      )}

      {/* Transient camera-busy retry in progress */}
      {permissionState === "granted" && effectiveCameraStatus === "retrying" && (
        <div
          className="camera-container"
          style={{ color: themeColors.textColor, fill: themeColors.textColor }}
          data-testid="qrscannercard-camera-retrying"
        >
          <div className="camera-icon" data-testid="qrscannercard-camera-retrying-icon">
            <span className="qr-camera-off" aria-hidden="true">
              <i className="bi bi-camera" />
              <i className="bi bi-slash-lg" />
            </span>
          </div>
          <p className="mt-3" data-testid="qrscannercard-camera-retrying-message">
            {S.QR_SCANNER_CARD.ERROR_CAMERA_BUSY_RETRY}
          </p>
          <div className="mt-3 w-100">
            <Button
              type="button"
              variant="primary"
              disabled
              className="btn btn-primary btn-width-full"
              data-testid="qrscannercard-camera-retrying-button"
            >
              <ClipLoader size={20} color={themeColors.btnTextColor} />
            </Button>
          </div>
        </div>
      )}

      {/* Camera failed after exhausting auto-retries */}
      {permissionState === "granted" && effectiveCameraStatus === "failed" && (
        <div
          className="camera-container"
          style={{ color: themeColors.textColor, fill: themeColors.textColor }}
          data-testid="qrscannercard-camera-failed"
        >
          <div className="camera-icon" data-testid="qrscannercard-camera-failed-icon">
            <span className="qr-camera-off" aria-hidden="true">
              <i className="bi bi-camera" />
              <i className="bi bi-slash-lg" />
            </span>
          </div>
          <p className="mt-3" data-testid="qrscannercard-camera-failed-message">
            {S.QR_SCANNER_CARD.CAMERA_NOT_AVAILABLE}
          </p>
          <div className="mt-3 w-100">
            <Button
              type="button"
              variant="primary"
              onClick={handleManualRetry}
              disabled={isLoading}
              className="btn btn-primary btn-width-full"
              data-testid="qrscannercard-camera-retry-button"
            >
              <i className="bi bi-arrow-repeat me-2" />
              {S.COMMON.RETRY}
            </Button>
          </div>
        </div>
      )}

      {/* Permission not granted — prompt / denied / unsupported / checking */}
      {permissionState !== "granted" && (
        <div
          className="camera-container"
          style={{ color: themeColors.textColor, fill: themeColors.textColor }}
          data-testid="qrscannercard-camera-container"
        >
          <div className="camera-icon" data-testid="qrscannercard-camera-icon">
            <i className="bi bi-qr-code" data-testid="qrscannercard-camera-icon-i" />
          </div>
          <p className="mt-3" data-testid="qrscannercard-camera-permission-message">
            {permissionMessage}
          </p>
          <p className="mt-2" data-testid="qrscannercard-camera-description">
            {S.QR_SCANNER_CARD.CAMERA_DISCLAIMER}
          </p>
          <div className="mt-3 w-100" data-testid="qrscannercard-enable-camera-wrapper">
            <Button
              type="button"
              id="enabledCamera"
              variant="primary"
              onClick={handleEnableCamera}
              disabled={
                isLoading ||
                permissionState === "unsupported" ||
                permissionState === "checking"
              }
              className="btn btn-primary btn-width-full"
              data-testid="qrscannercard-enable-camera-button"
            >
              {isLoading ? (
                <ClipLoader size={20} color={themeColors.btnTextColor} />
              ) : (
                S.COMMON.ENABLE_CAMERA
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default QRScannerCard;
